import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { ACCOUNT_QUERY_KEYS } from '@/lib/constants/account';
import { AccountApi } from '../AccountApi';
import type { CPUserViewData } from '@/lib/types/Auth';

/** Fetches all team members (CP users) for the currently selected account. */
export function useTeam() {
  const { selectedCId } = useAuth();

  const query = useQuery({
    queryKey: ACCOUNT_QUERY_KEYS.team(selectedCId ?? 0),
    queryFn: async () => {
      const result = await AccountApi.getUsers(selectedCId!);
      return result.data;
    },
    enabled: selectedCId != null,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const users: CPUserViewData[] = useMemo(
    () => query.data ?? [],
    [query.data],
  );

  return {
    users,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
}

/**
 * Finds a single team member by cpuserId from the cached team list.
 * The backend does not support fetching individual cpusers — the legacy app
 * always used the already-loaded list data for the edit flow.
 */
export function useTeamMember(cpuserId: number | null) {
  const { selectedCId } = useAuth();

  // Ensure the team list is loaded (will use cached data if available)
  const query = useQuery({
    queryKey: ACCOUNT_QUERY_KEYS.team(selectedCId ?? 0),
    queryFn: async () => {
      const result = await AccountApi.getUsers(selectedCId!);
      return result.data;
    },
    enabled: selectedCId != null && cpuserId != null,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const user = useMemo(() => {
    if (!query.data || cpuserId == null) return null;
    return query.data.find(u => u.core.cpuserId === cpuserId) ?? null;
  }, [query.data, cpuserId]);

  return {
    user,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
}
